/**
 * StatisticCard Component
 *
 * Reusable statistic card with icon, value, and optional trend indicator
 */

import React, { ReactNode } from 'react';
import classNames from 'clsx';
import { IoTrendingUp, IoTrendingDown } from 'react-icons/io5';
import { useCountUp } from '@/hooks/useCountUp';

export interface StatisticCardProps {
  /** Card title */
  title: string;

  /** Main value to display (must be a number) */
  value: number;

  /** Prefix to display before value (e.g., "£", "$") */
  prefix?: string;

  /** Suffix to display after value (e.g., "%", "K") */
  suffix?: string;

  /** Icon to display (React element) */
  icon?: ReactNode;

  /** Icon background color */
  iconBgColor?: string;

  /** Additional className for icon container */
  iconClassName?: string;

  /** Trend percentage */
  trend?: number;

  /** Trend label */
  trendLabel?: string;

  /** Custom className for the card */
  className?: string;

  /** Click handler */
  onClick?: () => void;

  /** Enable counting animation (default: true) */
  enableAnimation?: boolean;

  /** Animation duration in milliseconds (default: 2000) */
  animationDuration?: number;

  /** Number of decimal places for the animated value (default: 0) */
  decimals?: number;
}

export const StatisticCard: React.FC<StatisticCardProps> = ({
  title,
  value,
  prefix,
  suffix,
  icon,
  iconBgColor = '#ef4444',
  iconClassName,
  trend,
  trendLabel = 'vs last period',
  className,
  onClick,
  enableAnimation = true,
  animationDuration = 2000,
  decimals = 0,
}) => {
  const isPositiveTrend = trend !== undefined && trend >= 0;

  // Animate the value from 0 to the target value
  const animatedValue = useCountUp({
    end: value,
    duration: animationDuration,
    decimals,
    enabled: enableAnimation,
  });

  return (
    <div
      className={classNames(
        'rounded-lg p-5 bg-white transition-all duration-200',
        {
          'cursor-pointer hover:bg-gray-100': onClick,
        },
        className
      )}
      onClick={onClick}
    >
      <div className="flex items-center gap-3 justify-between">
        <div className="flex-1 gap-2">
          <div className="text-gray-700 whitespace-nowrap text-sm font-medium">{title}</div>
          <div className="text-2xl font-bold text-gray-900">
            {prefix}{animatedValue.toLocaleString()}{suffix}
          </div>
          {/* {trend !== undefined && (
            <div
              className={classNames('flex items-center gap-1 text-sm font-medium', {
                'text-green-600': isPositiveTrend,
                'text-red-600': !isPositiveTrend,
              })}
            >
              {isPositiveTrend ? (
                <IoTrendingUp size={16} />
              ) : (
                <IoTrendingDown size={16} />
              )}
              <span>
                {isPositiveTrend ? '+' : ''}
                {trend.toFixed(2)}%
              </span>
              <span className="text-gray-500 font-normal ml-1">{trendLabel}</span>
            </div>
          )} */}
        </div>
        {icon && (
          <div
            className={classNames(
              'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ',
              iconClassName
            )}
            style={{ backgroundColor: iconBgColor }}
          >
            <div className="text-white">{icon}</div>
          </div>
        )}
      </div>


    </div>
  );
};

export default StatisticCard;
